/**
 * All money in Volt is stored and transferred as integer kopecks (1 RUB = 100 kopecks).
 * Floating point rubles are never persisted.
 */

export const KOPECKS_IN_RUBLE = 100;

/** Rounding epsilon that protects `Math.ceil` from IEEE-754 artefacts (e.g. 3.0000000000000004). */
const EPSILON = 1e-9;

export function rublesToKopecks(rubles: number): number {
  return Math.round(rubles * KOPECKS_IN_RUBLE);
}

export function kopecksToRubles(kopecks: number): number {
  return kopecks / KOPECKS_IN_RUBLE;
}

/**
 * Converts a USD amount into kopecks, applying the FX rate and the admin markup.
 * Always rounds **up** so the platform never loses a fraction of a kopeck.
 */
export function usdToKopecks(usdAmount: number, usdRubRate: number, markupPercent: number): number {
  if (!Number.isFinite(usdAmount) || usdAmount <= 0) return 0;
  if (!Number.isFinite(usdRubRate) || usdRubRate <= 0) {
    throw new Error('usdRubRate must be a positive finite number');
  }
  if (!Number.isFinite(markupPercent) || markupPercent < 0) {
    throw new Error('markupPercent must be a non-negative finite number');
  }
  const rubles = usdAmount * usdRubRate * (1 + markupPercent / 100);
  return Math.ceil(rubles * KOPECKS_IN_RUBLE - EPSILON);
}

/** `12345` -> `123,45 ₽` */
export function formatKopecks(kopecks: number, locale = 'ru-RU'): string {
  const value = kopecksToRubles(kopecks);
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'RUB',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/** Compact form used in tight UI spots: `≈ 0,42 ₽`. */
export function formatKopecksApprox(kopecks: number, locale = 'ru-RU'): string {
  return `≈ ${formatKopecks(kopecks, locale)}`;
}
